import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, Mail, User, Globe, Users, MessageSquare, 
  Calendar, CheckCircle, Clock, Eye, Trash2, Search,
  Handshake, DollarSign, Filter, RefreshCw
} from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/auth';
import { formatDateTime, cn } from '../../lib/utils';
import { toast } from 'sonner';

export default function AdminPartnerInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const response = await api.getPartnerInquiries();
      setInquiries(response.data);
    } catch (error) {
      console.error('Failed to load partner inquiries:', error);
      toast.error('Failed to load partner inquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.updatePartnerInquiryStatus(id, newStatus);
      setInquiries(prev => prev.map(inq => 
        inq.id === id ? { ...inq, status: newStatus } : inq
      ));
      if (selectedInquiry?.id === id) {
        setSelectedInquiry(prev => ({ ...prev, status: newStatus }));
      }
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const filteredInquiries = inquiries.filter(inq => {
    const matchesSearch = 
      inq.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inq.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inq.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inq.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'in_progress': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'approved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const statusOptions = ['new', 'contacted', 'in_progress', 'approved', 'rejected'];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold text-white mb-2 flex items-center gap-3">
                <Handshake className="w-8 h-8 text-primary" />
                Partner Inquiries
              </h1>
              <p className="text-gray-400">Manage agency and SaaS partner applications</p>
            </div>
            <Button onClick={loadInquiries} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8"
        >
          {[
            { label: 'Total', count: inquiries.length, color: 'text-white' },
            { label: 'New', count: inquiries.filter(i => i.status === 'new').length, color: 'text-blue-400' },
            { label: 'Contacted', count: inquiries.filter(i => i.status === 'contacted').length, color: 'text-yellow-400' },
            { label: 'In Progress', count: inquiries.filter(i => i.status === 'in_progress').length, color: 'text-purple-400' },
            { label: 'Approved', count: inquiries.filter(i => i.status === 'approved').length, color: 'text-green-400' },
          ].map((stat, i) => (
            <div key={stat.label} className="glass rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.count}</p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex flex-col sm:flex-row gap-4 mb-6"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, business, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'new', 'contacted', 'in_progress', 'approved'].map(status => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status ? 'btn-primary' : ''}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
              </Button>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Inquiries List */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-4"
          >
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />
              ))
            ) : filteredInquiries.length === 0 ? (
              <div className="glass rounded-xl p-8 text-center">
                <Handshake className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No partner inquiries found</p>
              </div>
            ) : (
              filteredInquiries.map((inquiry, i) => (
                <motion.div
                  key={inquiry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => setSelectedInquiry(inquiry)}
                  className={cn(
                    "glass rounded-xl p-5 cursor-pointer transition-all hover:border-primary/50",
                    selectedInquiry?.id === inquiry.id ? "border-primary" : "border-white/10"
                  )}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary" />
                        {inquiry.business_name}
                      </h3>
                      <p className="text-sm text-gray-400">{inquiry.full_name}</p>
                    </div>
                    <span className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium border",
                      getStatusColor(inquiry.status)
                    )}>
                      {inquiry.status?.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />
                      {inquiry.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {inquiry.business_type}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDateTime(inquiry.created_at)}
                    </span>
                  </div>
                  
                  {inquiry.services_interested?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {inquiry.services_interested.slice(0, 3).map(service => (
                        <span key={service} className="px-2 py-0.5 rounded bg-white/5 text-xs text-gray-300">
                          {service}
                        </span>
                      ))}
                      {inquiry.services_interested.length > 3 && (
                        <span className="px-2 py-0.5 rounded bg-white/5 text-xs text-gray-400">
                          +{inquiry.services_interested.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </motion.div>

          {/* Detail Panel */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="lg:col-span-1"
          >
            {selectedInquiry ? (
              <div className="glass rounded-xl p-6 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-white">Inquiry Details</h2>
                  <span className={cn(
                    "px-3 py-1 rounded-full text-xs font-medium border",
                    getStatusColor(selectedInquiry.status)
                  )}>
                    {selectedInquiry.status?.replace('_', ' ')}
                  </span>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Business Name</label>
                    <p className="text-white font-medium">{selectedInquiry.business_name}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Contact</label>
                    <p className="text-white">{selectedInquiry.full_name}</p>
                    <a href={`mailto:${selectedInquiry.email}`} className="text-primary text-sm hover:underline">
                      {selectedInquiry.email}
                    </a>
                  </div>

                  {selectedInquiry.website && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Website</label>
                      <a href={selectedInquiry.website} target="_blank" rel="noopener noreferrer" 
                         className="text-primary text-sm hover:underline block truncate">
                        {selectedInquiry.website}
                      </a>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Business Type</label>
                      <p className="text-white text-sm">{selectedInquiry.business_type}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Clients</label>
                      <p className="text-white text-sm">{selectedInquiry.num_clients || 'Not specified'}</p>
                    </div>
                  </div>

                  {selectedInquiry.services_interested?.length > 0 && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Services Interested</label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {selectedInquiry.services_interested.map(service => (
                          <span key={service} className="px-2 py-1 rounded bg-primary/10 text-xs text-primary border border-primary/30">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedInquiry.message && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Message</label>
                      <p className="text-gray-300 text-sm mt-1 p-3 rounded-lg bg-white/5">
                        {selectedInquiry.message}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs text-gray-500 uppercase">Submitted</label>
                    <p className="text-gray-400 text-sm">{formatDateTime(selectedInquiry.created_at)}</p>
                  </div>

                  {/* Status Actions */}
                  <div className="pt-4 border-t border-white/10">
                    <label className="text-xs text-gray-500 uppercase mb-2 block">Update Status</label>
                    <div className="grid grid-cols-2 gap-2">
                      {statusOptions.map(status => (
                        <Button
                          key={status}
                          size="sm"
                          variant={selectedInquiry.status === status ? 'default' : 'outline'}
                          onClick={() => updateStatus(selectedInquiry.id, status)}
                          className={cn(
                            "text-xs",
                            selectedInquiry.status === status && getStatusColor(status)
                          )}
                        >
                          {status.replace('_', ' ')}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-2">
                    <a href={`mailto:${selectedInquiry.email}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="glass rounded-xl p-8 text-center sticky top-24">
                <Eye className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">Select an inquiry to view details</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
